import React, { createContext, useContext, useState, useEffect } from 'react';
import AuthService from '../services/auth.service';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage on app start
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const currentUser = AuthService.getCurrentUser();
        if (currentUser && currentUser.accessToken) {
          // Check if token is still valid
          if (!AuthService.isTokenExpired()) {
            setUser(currentUser);
          } else {
            // Token expired, clear user but don't redirect immediately
            console.log('Token expired during initialization');
            //AuthService.logout();
            //setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      setIsLoading(true);
      const response = await AuthService.login(credentials.emailSignIn, credentials.passwordSignIn);
      if (response.accessToken) {
        setUser(response);
        return response;
      }
      throw new Error('No access token received');
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    try {
      AuthService.logout();
      setUser(null);
    } catch (error) {
      console.error('Error during logout:', error);
      setUser(null);
    }
  };

  // Update user data
  const updateUser = (userData) => {
    try {
      const updatedUser = AuthService.updateCurrentUser(userData);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  // Check if user is logged in
  const isLoggedIn = !!user && !!user.accessToken;

  // Check if user is on dashboard
  const isOnDashboard = window.location.pathname.startsWith('/user/');

  const value = {
    user,
    isLoggedIn,
    isOnDashboard,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;